import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { CheckIcon, PlusIcon, XMarkIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { checkApi } from '../../Authentication/AxiosInstance';
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const MAX_PREFERRED = 10;

// Centralize API paths so they can be easily adjusted
const API = {
  profile: '/candidates/candidate-details/my_profile/',
  updateProfile: '/candidates/candidate-details/update_profile/',
  institutes: `${API_ENDPOINT}/masters/institutes/`, // supports query params: type, state, city, search
};

const SDPrefColleges = ({ candidateId, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [preferredColleges, setPreferredColleges] = useState([]);
  const [candidateExams, setCandidateExams] = useState([]);

  // Filters
  const [allInstitutes, setAllInstitutes] = useState([]);
  const [instituteTypes, setInstituteTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [instituteOptions, setInstituteOptions] = useState([]);

  const [selectedType, setSelectedType] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const canAddMore = preferredColleges.length < MAX_PREFERRED;

  const loadPreferred = async () => {
    try {
      setLoading(true);
      const res = await checkApi.get(API.profile);
      console.log('Profile API response:', res.data);
      const collegeDetails = res.data.preferred_college_details || [];
      console.log('Preferred college details:', collegeDetails);
      setPreferredColleges(collegeDetails);
    } catch (e) {
      console.log('error:', e);
      setError('Failed to load preferred colleges.');
    } finally {
      setLoading(false);
    }
  };

  const loadCandidateExams = async () => {
    try {
      const res = await checkApi.get('/candidates/candidate-exams/my_exams/');
      const exams = res.data || [];
      setCandidateExams(exams);
      console.log('Candidate exams:', exams);
    } catch (e) {
      console.log('error loading exams:', e);
    }
  };  
  console.log('preferred Colleges:', preferredColleges);
  const loadAllData = async () => {
    try {
      console.log('Loading institutes data from:', API.institutes);
      const res = await checkApi.get(API.institutes);
      console.log('API response:', res);
      const institutes = res.data || [];
      setAllInstitutes(institutes);
      console.log('Loaded institutes:', institutes.length, institutes.slice(0, 3));
      
      // Debug: Check what fields are available in the first institute
      if (institutes.length > 0) {
        console.log('First institute fields:', Object.keys(institutes[0]));
        console.log('First institute data:', institutes[0]);
      }
      
      // Extract unique types from institutes
      const uniqueTypes = [...new Set(institutes.map(inst => inst.inst_type).filter(Boolean))];
      // Sort alphabetically
      uniqueTypes.sort((a, b) => a.localeCompare(b));
      setInstituteTypes(uniqueTypes.map(type => ({ value: type, label: type })));
      console.log('institute types:', uniqueTypes);

      // Extract unique states from institutes
      console.log('Raw state values:', institutes.map(inst => inst.inst_state).slice(0, 10));
      const uniqueStates = [...new Set(institutes.map(inst => inst.inst_state).filter(Boolean))];
      // Sort alphabetically
      uniqueStates.sort((a, b) => a.localeCompare(b));
      setStates(uniqueStates.map(state => ({ value: state, label: state })));
      console.log('All states loaded:', uniqueStates);

      // Extract unique cities from institutes
      const uniqueCities = [...new Set(institutes.map(inst => inst.inst_city).filter(Boolean))];
      // Sort alphabetically
      uniqueCities.sort((a, b) => a.localeCompare(b));
      setCities(uniqueCities.map(city => ({ value: city, label: city })));
      console.log('All cities loaded:', uniqueCities);
    } catch (e) {
      console.log('error:', e);
      setError('Failed to load institutes data.');
    }
  };
  
  const filterInstitutes = () => {
    let filtered = allInstitutes;
    
    // Filter by candidate's selected exams first
    filtered = filterByCandidateExams(filtered);
    
    // Filter by type if selected
    if (selectedType?.value) {
      filtered = filtered.filter(inst => inst.inst_type === selectedType.value);
    }
    
    // Filter by state if selected
    if (selectedState?.value) {
      filtered = filtered.filter(inst => inst.inst_state === selectedState.value);
    }
    
    // Filter by city if selected
    if (selectedCity?.value) {
      filtered = filtered.filter(inst => inst.inst_city === selectedCity.value);
    }
    
    // Convert to options format
    const options = filtered.map(inst => ({ 
      value: inst.id, 
      label: inst.inst_name || 'Unknown Institute'
    }));
    
    // Sort alphabetically
    if (options && Array.isArray(options)) {
      options.sort((a, b) => {
        const labelA = a.label || '';
        const labelB = b.label || '';
        return labelA.localeCompare(labelB);
      });
    }
    setInstituteOptions(options || []);
  };

  const filterByCandidateExams = (institutes) => {
    if (candidateExams.length === 0) {
      console.log('No candidate exams found, showing all institutes');
      return institutes;
    }

    const examNames = candidateExams.map(exam => exam.exam_name);
    console.log('Candidate exam names:', examNames);
    console.log('Total institutes before filtering:', institutes.length);

    const filtered = institutes.filter(inst => {
      const instId = inst.id;
      const instCode = inst.inst_code || '';

      // Check if institute matches any of the candidate's selected exams
      for (const examName of examNames) {
        if (examName === 'MHTCET' && instId > 500) {
          console.log(`Institute ${instId} (${inst.inst_name}) matches MHTCET (ID > 500)`);
          return true;
        }
        if (examName === 'JEEMAINS' && instId >= 100 && instId <= 500) {
          console.log(`Institute ${instId} (${inst.inst_name}) matches JEEMAINS (ID 100-500)`);
          return true;
        }
        if (examName === 'COMEDK' && instCode.startsWith('E')) {
          console.log(`Institute ${instId} (${inst.inst_name}, ${instCode}) matches COMEDK (code starts with E)`);
          return true;
        }
      }
      return false;
    });

    console.log('Filtered institutes count:', filtered.length);
    return filtered;
  };

  useEffect(() => {
    loadPreferred();
    loadAllData();
    loadCandidateExams();
  }, []);

  // Filter institutes when candidate exams change
  useEffect(() => {
    if (candidateExams.length > 0 && allInstitutes.length > 0) {
      console.log('Candidate exams changed, filtering institutes');
      filterInstitutes();
    }
  }, [candidateExams, allInstitutes]);

  useEffect(() => {
    console.log('useEffect triggered - selectedType:', selectedType, 'allInstitutes length:', allInstitutes.length);
    
    // Reset city and institute when type changes
    setSelectedCity(null);
    setSelectedInstitute(null);
    
    // Update states based on selected type
    if (selectedType?.value && allInstitutes.length > 0) {
      console.log('Filtering states for type:', selectedType.value);
      const typeStates = [...new Set(
        allInstitutes
          .filter(inst => inst.inst_type === selectedType.value)
          .map(inst => inst.inst_state)
          .filter(Boolean)
      )];
      // Sort alphabetically
      typeStates.sort((a, b) => a.localeCompare(b));
      setStates(typeStates.map(state => ({ value: state, label: state })));
      console.log('Filtered states for type:', selectedType.value, typeStates);
    } else if (allInstitutes.length > 0) {
      // Show all states if no type selected
      console.log('Showing all states');
      const allStates = [...new Set(allInstitutes.map(inst => inst.inst_state).filter(Boolean))];
      // Sort alphabetically
      allStates.sort((a, b) => a.localeCompare(b));
      setStates(allStates.map(state => ({ value: state, label: state })));
      console.log('All states:', allStates);
    } else {
      console.log('No institutes loaded yet');
    }
    
    filterInstitutes();
  }, [selectedType, allInstitutes]);

  useEffect(() => {
    // Reset institute when state changes
    setSelectedInstitute(null);
    
    // Update cities based on selected state
    if (selectedState?.value && allInstitutes.length > 0) {
      let filteredInstitutes = allInstitutes;
      
      // Filter by type if selected
      if (selectedType?.value) {
        filteredInstitutes = filteredInstitutes.filter(inst => inst.inst_type === selectedType.value);
      }
      
      // Filter by state
      filteredInstitutes = filteredInstitutes.filter(inst => inst.inst_state === selectedState.value);
      
      const stateCities = [...new Set(
        filteredInstitutes
          .map(inst => inst.inst_city)
          .filter(Boolean)
      )];
      // Sort alphabetically
      stateCities.sort((a, b) => a.localeCompare(b));
      setCities(stateCities.map(city => ({ value: city, label: city })));
      console.log('Filtered cities for state:', selectedState.value, stateCities);
    } else {
      setCities([]);
    }
    
    filterInstitutes();
  }, [selectedState, selectedType, allInstitutes]);

  useEffect(() => {
    // Reset institute when city changes
    setSelectedInstitute(null);
    filterInstitutes();
  }, [selectedCity, selectedType, selectedState, allInstitutes]);


  const onInstituteSearch = (input) => {
    // Filter institutes based on search input
    let filtered = allInstitutes;
    
    // Apply existing filters
    if (selectedType?.value) {
      filtered = filtered.filter(inst => inst.inst_type === selectedType.value);
    }
    console.log('filtered:', filtered);
    if (selectedState?.value) {
      filtered = filtered.filter(inst => inst.inst_state === selectedState.value);
    }
    if (selectedCity?.value) {
      filtered = filtered.filter(inst => inst.inst_city === selectedCity.value);
    }
    
    // Apply search filter
    if (input) {
      filtered = filtered.filter(inst => 
        (inst.inst_name || '').toLowerCase().includes(input.toLowerCase())
      );
    }
    
    const options = filtered.map(inst => ({ 
      value: inst.id, 
      label: inst.inst_name || 'Unknown Institute'
    }));
    
    // Sort alphabetically
    if (options && Array.isArray(options)) {
      options.sort((a, b) => {
        const labelA = a.label || '';
        const labelB = b.label || '';
        return labelA.localeCompare(labelB);
      });
    }
    setInstituteOptions(options || []);
  };

  const handleAdd = async () => {
    setError('');
    setSuccess('');
    if (!candidateId) { setError('Missing candidate.'); return; }
    if (!canAddMore) { setError(`Maximum of ${MAX_PREFERRED} colleges reached.`); return; }
    if (!selectedInstitute?.value) { setError('Select an institute.'); return; }
    try {
      setSaving(true);
      
      // Get current preferred colleges
      const currentResponse = await checkApi.get(API.profile);
      const currentColleges = currentResponse.data.candidate_preferred_colleges || [];
      console.log('Current colleges from API:', currentColleges);
      
      // Add new college if not already present
      if (!currentColleges.includes(selectedInstitute.value)) {
        const updatedColleges = [...currentColleges, selectedInstitute.value];
        
        // Update using update_profile endpoint
        await checkApi.put(API.updateProfile, {
          section_type: 'colleges',
          candidate_preferred_colleges: updatedColleges
        });
        
        setSelectedInstitute(null);
        setSuccess('Added preferred college.');
        await loadPreferred();
        if (onUpdated) onUpdated();
        setTimeout(() => setSuccess(''), 2500);
    } else {
        setError('College already in preferred list.');
      }
    } catch (e) {
      console.log('error:', e);
      setError('Failed to add preferred college.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (collegeId) => {
    if (!collegeId) return;
    try {
      // Get current preferred colleges
      const currentResponse = await checkApi.get(API.profile);
      const currentColleges = currentResponse.data.candidate_preferred_colleges || [];
      console.log('Current colleges for delete:', currentColleges);
      
      // Remove the college from the list
      const updatedColleges = currentColleges.filter(id => id !== collegeId);
      
      // Update using update_profile endpoint
      await checkApi.put(API.updateProfile, {
        section_type: 'colleges',
        candidate_preferred_colleges: updatedColleges
      });
      
      await loadPreferred();
      if (onUpdated) onUpdated();
    } catch {
      setError('Failed to remove preferred college.');
    }
  };

  const remaining = useMemo(() => Math.max(0, MAX_PREFERRED - preferredColleges.length), [preferredColleges.length]);
  return (
    <div className="inline-block w-full max-w-3xl mx-auto rounded-lg shadow-md shadow-orange-200">
      <div className="bg-tertiary-600 rounded-xl">
        {/* Header */}
        <div className="flex items-center justify-between shadow-md shadow-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-orange-400 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">3</span>
            </div>
            <h3 className="flex items-center text-lg font-semibold text-gray-900">My Preferred Colleges</h3>
            <div className="flex items-center text-sm text-gray-600 gap-2">
              <span>Remaining:</span>
              <span className="font-semibold">{remaining}</span>
              <span>/ {MAX_PREFERRED}</span>
            </div>
          </div>
          {/* Expand/Collapse Button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex items-center justify-center w-10 h-10 bg-orange-400 text-white rounded-lg hover:bg-orange-800 transition-colors"
            title={isCollapsed ? "Expand section" : "Collapse section"}
          >
            {isCollapsed ? (
              <ChevronDownIcon className="w-5 h-5" />
            ) : (
              <ChevronUpIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>
      )}
      {success && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{success}</div>
      )}

      {/* Collapsible Content */}
      {!isCollapsed && (
          <>
            {/* List */}
            <div className="mt-4">
          {loading ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading preferred colleges...</p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              {preferredColleges.length === 0 && (
                <span className="w-full px-3 py-1 rounded-full bg-gray-200 text-secondary-900">No preferred colleges yet</span>
              )}
               {/* Desktop Table View */}
               <div className="hidden md:block">
                 <table className="w-full table-fixed">
                    <thead className="bg-orange-50 text-blue-900 text-xs md:text-sm">
                     <tr>
                        <th className="px-4 py-2 text-center w-16">#</th>
                       <th className="px-4 py-2 text-left w-1/2">Institute Name</th>
                        <th className="px-4 py-2 text-left">Location</th>
                        <th className="px-4 py-2 text-center">Remove</th>
                     </tr>
                   </thead>
                    <tbody className="text-xs md:text-sm">
                     {preferredColleges.map((item, index) => (
                       <tr key={item.id} className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-transparent' : 'bg-orange-50/50'}`}>
                          <td className="px-4 py-2 text-center font-medium text-gray-600">{index + 1}</td>
                         <td className="px-4 py-2 whitespace-normal break-words align-top">{item.institute_name}</td>
                          <td className="px-4 py-2 whitespace-nowrap">{item.city}, &nbsp;{item.state_abbrev}</td>
                          <td className="px-4 py-2 text-center">
                           <button onClick={() => handleDelete(item.id)} 
                                   className="text-red-600 hover:text-red-800" title="Remove">
                             <TrashIcon className="w-4 h-4" />
                           </button>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>

               {/* Mobile Card View */}
               <div className="md:hidden space-y-3">
                 {preferredColleges.map((item, index) => (
                   <div key={item.id} className={`p-4 rounded-lg border ${index % 2 === 0 ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                     <div className="flex items-start justify-between">
                       <div className="flex-1 min-w-0">
                         <div className="flex items-center gap-2 mb-2">
                           <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                           <h3 className="text-sm font-semibold text-gray-900 truncate">{item.institute_name}</h3>
                         </div>
                         <p className="text-sm text-gray-600">{item.city}, {item.state_abbrev}</p>
                       </div>
                       <button 
                         onClick={() => handleDelete(item.id)} 
                         className="ml-3 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                         title="Remove"
                       >
                         <TrashIcon className="w-5 h-5" />
                       </button>
                     </div>
                   </div>
                 ))}
               </div>
            </div>
          )}
        </div>

            {/* Add - Only show if can add more */}
            {canAddMore ? (
              <div className="mt-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="col-span-full">
              <label className="text-sm font-medium text-gray-700 mb-1">Institute Type</label>
              <Select
                classNamePrefix="rsel"
                options={instituteTypes}
                value={selectedType}
                onChange={setSelectedType}
                placeholder="Select type"
                isClearable
              />
            </div>
            <div className="col-span-1">
              <label className="text-sm font-medium text-gray-700 mb-1">State</label>
              <Select
                classNamePrefix="rsel"
                options={states}
                value={selectedState}
                onChange={setSelectedState}
                placeholder="Select state"
                isClearable
              />
            </div>
            <div className="col-span-1">
              <label className="text-sm font-medium text-gray-700 mb-1">City</label>
              <Select
                classNamePrefix="rsel"
                options={cities}
                value={selectedCity}
                onChange={setSelectedCity}
                placeholder="Select city"
                isClearable
                isDisabled={!selectedState}
              />
            </div>
            <div className="col-span-full">
              <label className="text-sm font-medium text-gray-700 mb-1">Institute</label>
              <Select
                classNamePrefix="rsel"
                options={instituteOptions}
                value={selectedInstitute}
                onChange={setSelectedInstitute}
                onInputChange={(val) => onInstituteSearch(val)}
                placeholder="Search and select institute"
                isClearable
                isDisabled={!selectedType && !selectedState && !selectedCity}
                filterOption={null}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              disabled={!canAddMore || saving}
              onClick={handleAdd}
              className="buttonClass flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <PlusIcon className="w-4 h-4" />
                  <span>Add Preferred</span>
                </>
              )}
            </button>
          </div>
        </div>
            ) : (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 text-orange-900">
                  <CheckIcon className="w-5 h-5" />
                  <span className="font-medium">Maximum preferred colleges reached</span>
                </div>
                <p className="text-sm text-orange-700 mt-1">
                  You have reached the maximum of {MAX_PREFERRED} preferred colleges. Remove some to add new ones.
                </p>
              </div>
            )}
          </>
      )}
    </div>
  );
};

export default SDPrefColleges;

